import {createSlice, PayloadAction, createAsyncThunk} from '@reduxjs/toolkit';
import { Header, ITableData,TypeOfTableData } from '../../pricing-simulator/types/generic-table.model';
import { SummaryStripModuleObject } from '../../constants/constant';
import { IFilterObj } from 'src/components/filters';
import { ICombinationModel } from 'src/components/filters';
import { FormField2, IDynamicFormValue } from 'src/lib';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';

import { fetchFilterFields } from '../../common/features';
import { getClusterSummaryTableData } from '../api/cluster-api';
import { IProductComment } from '../../planner_v2/types/ppg-sku-drawer.model';
import { deleteScenarios } from '../../scenario-manager/api/scenario-manager-api';
import { RowSelectionState } from '@tanstack/table-core';


export const fetchClusterSummaryData = createAsyncThunk(
  'fetchDataclusterSummary',
  async ({
    sourceId
  }:{
    sourceId: number;
  }) => {
    const response = await getClusterSummaryTableData(sourceId)
    return response
  }
);

const deleteSelectedScenarios = createAsyncThunk(
  'deleteScenarios',
  async ({
    scenarioIds,
    sourceId
  }: {
    scenarioIds: number[];
    sourceId: number;
  }) => {
    const response = await deleteScenarios(
      { scenarios: scenarioIds },
      sourceId
    );
    return {
      scenarioIds,
      response: response.data
    };
  }
);

interface IClusterSummaryState {
   clusterSummaryTableData?: ITableData;
   fetchingTableData: boolean;
    selectedSourceKey: number;
    inputCols: Header[];
    isDeletingScenario: boolean;
    sourceScenarioMap: { [sourceId: string]: any[] };
    // Store selection state for each source
    sourceSelectionMap: { [sourceId: string]: RowSelectionState };
    // Store selected row data for each source
    sourceSelectedRowsMap: { [sourceId: string]: { [key: string]: string | number }[] };
}

const initialState: IClusterSummaryState = {
    fetchingTableData: false,
    selectedSourceKey: null,
    inputCols: [],
    clusterSummaryTableData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: { enabled: false, label: '' },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    summary_strip: {
      metadata: [],
      data: {
        internal_dt: 0,
        external_dt: 0,
        renovation: SummaryStripModuleObject,
        pricing: SummaryStripModuleObject,
        delist: SummaryStripModuleObject,
        distribution: SummaryStripModuleObject,
        innovation_table: SummaryStripModuleObject
      }
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  isDeletingScenario: false,
  sourceScenarioMap: {},
  sourceSelectionMap: {},
  sourceSelectedRowsMap: {},
};

const clusterSummarySlice = createSlice({
  name: 'clusterSummary',
  initialState,
  reducers: {
    setSelectedSourceKey(state, action) {
      state.selectedSourceKey = action.payload;
    },
    setIsDeletingScenario(state, action) {
      state.isDeletingScenario = action.payload;
    },
    //  action to store selection state for a specific source
    setSourceSelectionState(state, action: PayloadAction<{
      sourceId: number;
      selectionState: RowSelectionState;
    }>) {
      const { sourceId, selectionState } = action.payload;
      state.sourceSelectionMap[sourceId] = selectionState;
    },
    //  action to store selected row data for a specific source
    setSourceSelectedRows(state, action: PayloadAction<{
      sourceId: number;
      selectedRows: { [key: string]: string | number }[];
    }>) {
      const { sourceId, selectedRows } = action.payload;
      state.sourceSelectedRowsMap[sourceId] = selectedRows;
    },
    resetscenarioSelectionState(state) {
      state.sourceSelectedRowsMap = {};
      //state.sourceScenarioMap = {};
      state.sourceSelectionMap = {};
      state.selectedSourceKey = null;

    },
    updateClusterSummaryNote: (state, 
        action: PayloadAction<{
          scenario_id:number;
          source_id: number;
          noteData:IProductComment;
        }>) => {
      const { scenario_id,source_id, noteData } = action.payload;
       if (state.sourceScenarioMap[source_id]) {
        state.sourceScenarioMap[source_id] = state.sourceScenarioMap[source_id].map((scenario: any) => {
          if (scenario.parent?.data?.scenario_id === scenario_id) {
            return {
              ...scenario,
              parent: {
                ...scenario.parent,
                data: {
                  ...scenario.parent.data,
                  note: noteData
                }
              }
            };
          }
          return scenario;
        });
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClusterSummaryData.pending, (state) => {
        state.fetchingTableData = true;
      })
      .addCase(fetchClusterSummaryData.fulfilled, (state, action) => {
        state.fetchingTableData = false;
       state.clusterSummaryTableData = action.payload;

        for (const row of action.payload.row) {
          const sourceId = row.parent.source_id;
          state.sourceScenarioMap[sourceId] = row.children || [];
        }
         state.selectedSourceKey = action.payload.row[0].parent.source_id;

        state.clusterSummaryTableData = 
          action.payload;
        state.inputCols = filterColumnsForToggleView(
                  [
                    TypeOfTableData.DISPLAY,
                    TypeOfTableData.INPUT,
                    TypeOfTableData.ACTION
                  ],
                  action.payload.header,
                  ''
                );
        
        
        })

      .addCase(fetchClusterSummaryData.rejected, (state) => {
        state.fetchingTableData = false;
      })
      .addCase(deleteSelectedScenarios.pending, (state) => {
        state.isDeletingScenario = false;
      })
      .addCase(deleteSelectedScenarios.fulfilled, (state, action)=>{
         state.isDeletingScenario = true; 
         const selectedRowIds = action.payload.scenarioIds;
         Object.keys(state.sourceScenarioMap).forEach(sourceId => {
            state.sourceScenarioMap[sourceId] = state.sourceScenarioMap[sourceId].filter(
                (selectedSourceKey) => !selectedRowIds.includes(selectedSourceKey.parent.data.scenario_id)
            );
            
            // Clear selection for sources that had deleted scenarios
            if (state.sourceSelectedRowsMap[sourceId]) {
                const updatedSelectedRows = state.sourceSelectedRowsMap[sourceId].filter(
                    (row) => !selectedRowIds.includes(Number(row.scenario_id))
                );
                state.sourceSelectedRowsMap[sourceId] = updatedSelectedRows;
                
                // If no rows selected, clear the selection state
                if (updatedSelectedRows.length === 0) {
                    delete state.sourceSelectionMap[sourceId];
                    delete state.sourceSelectedRowsMap[sourceId];
                }
            }
        });
      })
       .addCase(deleteSelectedScenarios.rejected, (state) => {
        state.isDeletingScenario = false;
      })

  }
});

export const {
  setSelectedSourceKey,
  setIsDeletingScenario,
  setSourceSelectionState,
  setSourceSelectedRows,
  updateClusterSummaryNote,
  resetscenarioSelectionState
} = clusterSummarySlice.actions;

export{
  deleteSelectedScenarios
}

export default clusterSummarySlice;